// frontend/src/components/LetterPreviewModal.js
import React, { useState, useEffect, useRef } from 'react';
import { FileText, X, Clock, AlertCircle, ExternalLink, Download } from 'lucide-react';
import api from '../services/api';
import Portal from './UI/Portal';
import PdfViewer from './UI/PdfViewer';

/**
 * Shared modal for previewing an official letter PDF inline (HR letters +
 * asset acknowledgement letters).
 *
 * DS compliance (see frontend/src/styles/design-system-reference.md):
 * - Wrapped in <Portal>
 * - Uses .modal-overlay / .modal-container / .modal-header / .modal-title
 * - PDF via <PdfViewer> (pdf.js) — never a native <iframe>
 * - Theme-aware: accent icon + close button match page theme (employee = blue,
 *   admin = red). Default theme is 'employee'.
 *
 * Props:
 *   letterId          – UUID of the letter record
 *   source            – 'asset_ack' (default) | 'hr_letter' — selects preview API
 *   title             – optional header title override
 *   letterTypeDisplay – letter type label (used as title when `title` omitted)
 *   letterReference   – human-readable reference (e.g. WT/HR/ARA/202602)
 *   assetTag          – asset tag shown in the header subtitle (asset_ack)
 *   onClose           – called when the user dismisses the modal
 *   onDownload        – optional; header + PdfViewer toolbar download action
 *   theme             – 'employee' (default) | 'admin'
 *   allLettersHref    – (optional) link shown in the header for "All letters".
 *                       Defaults to the employee profile documents tab.
 *                       Pass null to hide the link entirely.
 */
const LetterPreviewModal = ({
  letterId,
  source = 'asset_ack',
  title,
  letterTypeDisplay,
  letterReference,
  assetTag,
  onClose,
  onDownload,
  theme = 'employee',
  allLettersHref = '/profile?tab=documents&subTab=letters',
}) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const blobUrlRef = useRef(null);

  const isAdmin = theme === 'admin';
  const isAssetAck = source === 'asset_ack';
  const accentText = isAdmin ? 'text-red-600' : 'text-blue-600';
  const accentHover = isAdmin ? 'hover:text-red-600' : 'hover:text-blue-600';
  const focusRing = isAdmin
    ? 'focus-visible:ring-red-500'
    : 'focus-visible:ring-blue-500';
  const closeClass = isAdmin ? 'modal-close-admin' : 'modal-close';
  const iconButtonClass = isAdmin ? 'icon-button-admin' : 'icon-button';
  const spinnerBorderClass = isAdmin
    ? 'border-red-800 border-t-transparent'
    : 'border-blue-800 border-t-transparent';

  const resolvedTitle =
    title
    || letterTypeDisplay
    || (isAssetAck ? 'Acknowledgement Letter' : 'HR Letter');

  const fileName = isAssetAck
    ? `asset_ack_${letterId}.pdf`
    : `hr_letter_${letterId}.pdf`;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const previewRequest = isAssetAck
      ? api.previewAssetAcknowledgementLetter(letterId)
      : api.previewHRLetter(letterId);

    previewRequest
      .then((res) => {
        if (cancelled) return;
        const objectUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        blobUrlRef.current = objectUrl;
        setBlobUrl(objectUrl);
      })
      .catch(async (err) => {
        if (cancelled) return;
        let message = 'Failed to load letter. Please try again.';
        try {
          const data = err?.response?.data;
          if (data instanceof Blob || (data && typeof data.text === 'function')) {
            const text = await data.text();
            try {
              const json = JSON.parse(text || '{}');
              message = json.detail || json.error || json.message || message;
            } catch {
              if (text && text.trim().length < 500) message = text.trim();
            }
          } else if (typeof data === 'object' && data !== null) {
            message = data.detail || data.error || data.message || message;
          }
        } catch {
          /* keep default message */
        }
        setError(message);
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [letterId, isAssetAck]);

  const handleDownload = async () => {
    if (!onDownload || downloading) return;
    setDownloading(true);
    try {
      await onDownload();
    } finally {
      setDownloading(false);
    }
  };

  const subtitleParts = [];
  if (assetTag) subtitleParts.push({ key: 'tag', node: <span className="font-medium">{assetTag}</span> });
  if (letterReference) subtitleParts.push({ key: 'ref', node: <span>{letterReference}</span> });

  return (
    <Portal>
      <div className="modal-overlay">
        {/*
          DS note: unlike form modals (which should shrink to fit their
          content), a document viewer is a *content-first* modal and should
          claim most of the viewport *vertically* so the PDF is actually
          readable.

          Sizing strategy (calibrated to an A4 portrait page):
            - width: we fall back to .modal-container's baseline `max-w-4xl`
              (~896px) — wider than that just produces gray gutters on
              either side of the A4 page and makes the modal feel bloated on
              widescreen laptops. No `modal-container-xl` override.
            - `h-[92vh]` gives the iframe a concrete vertical size to
              `flex-1` into; without it the PDF was ~150px tall (iframe's
              default) and left a huge empty body.

          Mobile note: the responsive cap `max-height: calc(100dvh - 1rem)`
          in index.css still clamps total height below viewport on small
          screens. We use `h-[92vh]` (hard height) rather than a hazardous
          `min-h-*` override — 92vh is always < 100dvh so it never fights
          the mobile clamp.
        */}
        <div className="modal-container h-[92vh]">
          {/* Header */}
          <div className="modal-header">
            <div className="min-w-0">
              <h3 className="modal-title flex items-center gap-2">
                <FileText className={`w-5 h-5 ${accentText} flex-shrink-0`} />
                <span className="truncate">{resolvedTitle}</span>
              </h3>
              {subtitleParts.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  {subtitleParts.map((part, idx) => (
                    <React.Fragment key={part.key}>
                      {idx > 0 && <span className="mx-1">·</span>}
                      {part.node}
                    </React.Fragment>
                  ))}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {onDownload && (
                <button
                  type="button"
                  onClick={handleDownload}
                  disabled={downloading || loading || !!error}
                  className={iconButtonClass}
                  title="Download letter"
                  aria-label="Download letter"
                >
                  {downloading
                    ? <div className={`w-4 h-4 border-2 ${spinnerBorderClass} rounded-full animate-spin`} />
                    : <Download className="w-4 h-4" />}
                </button>
              )}
              {allLettersHref && (
                <a
                  href={allLettersHref}
                  className={`inline-flex items-center gap-1 text-xs text-gray-600 ${accentHover} transition-colors border border-gray-200 rounded-lg px-2.5 py-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${focusRing}`}
                  title="View all letters"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  All letters
                </a>
              )}
              <button
                type="button"
                onClick={onClose}
                className={closeClass}
                title="Close"
                aria-label="Close letter preview"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Body — PDF viewer fills the remaining vertical space.
              We intentionally override .modal-content padding/overflow so the
              iframe runs edge-to-edge, following the split-screen modal
              pattern (`!p-0 !overflow-hidden`). */}
          <div className="modal-content !p-0 !overflow-hidden bg-gray-100 flex flex-col min-h-0">
            {loading && (
              <div className="flex items-center justify-center flex-1 text-gray-500 gap-2">
                <Clock className="w-5 h-5 animate-spin" />
                <span className="text-sm">Loading letter…</span>
              </div>
            )}
            {error && (
              <div className="flex flex-col items-center justify-center flex-1 text-red-600 gap-2 px-6 text-center">
                <AlertCircle className="w-5 h-5" />
                <span className="text-sm">{error}</span>
              </div>
            )}
            {blobUrl && !loading && !error && (
              <div className="w-full flex-1 min-h-0">
                <PdfViewer
                  src={blobUrl}
                  title={resolvedTitle}
                  fileName={fileName}
                  theme={theme}
                  onDownload={onDownload ? handleDownload : undefined}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default LetterPreviewModal;
